// New chat dialog — extracted from DMView.js to bring its cyclomatic
// complexity below the code-quality threshold. Behaviour is unchanged:
// debounced 250ms search over /profiles, exclude self, prevent starting
// a chat with a peer that has never published their X25519 DM key.

import React, { useEffect, useState } from "react";
import { Loader2, Search, Shield } from "lucide-react";
import { openConversation, searchProfiles } from "../lib/dmApi";
import { IdentityRecoveryDialog } from "./IdentityRecoveryDialog";

const SEARCH_DEBOUNCE_MS = 250;
const MIN_SEARCH_LEN = 2;

function useProfileSearch(query, excludeIdentityId) {
  const [state, setState] = useState({ results: [], loading: false, error: null });
  useEffect(() => {
    const term = query.trim();
    if (term.length < MIN_SEARCH_LEN) {
      setState({ results: [], loading: false, error: null });
      return undefined;
    }
    let cancelled = false;
    setState((s) => ({ ...s, loading: true, error: null }));
    const t = setTimeout(async () => {
      try {
        const items = await searchProfiles(term);
        if (cancelled) return;
        const filtered = items.filter((p) => p.identityId !== excludeIdentityId);
        setState({ results: filtered, loading: false, error: null });
      } catch (e) {
        if (!cancelled) setState({ results: [], loading: false, error: e?.message || "Errore ricerca" });
      }
    }, SEARCH_DEBOUNCE_MS);
    return () => { cancelled = true; clearTimeout(t); };
  }, [query, excludeIdentityId]);
  return state;
}

function ProfileResultRow({ profile, onSelect }) {
  return (
    <button
      onClick={() => onSelect(profile)}
      className="w-full flex items-center gap-3 py-2 px-2 rounded-2xl hover:bg-cream text-left"
      data-testid={`dm-new-result-${profile.identityId}`}
    >
      {profile.avatar ? (
        <img src={profile.avatar} alt="" className="w-9 h-9 rounded-full object-cover" />
      ) : (
        <div className="w-9 h-9 rounded-full bg-ink text-white flex items-center justify-center font-semibold text-xs">
          {(profile.displayName || "?").slice(0, 1).toUpperCase()}
        </div>
      )}
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold truncate">{profile.displayName}</div>
        <div className="text-[10px] font-mono text-muted truncate">{profile.identityId}</div>
      </div>
      {profile.x25519PublicKeyHex
        ? <Shield size={13} className="text-emerald-600" title="Cifratura pronta" />
        : <span className="text-[10px] text-muted">no e2e</span>}
    </button>
  );
}

function ResultsList({ query, loading, results, onSelect }) {
  if (loading) {
    return <div className="flex items-center gap-2 text-xs text-muted py-3"><Loader2 size={13} className="animate-spin" /> Cerco…</div>;
  }
  const term = query.trim();
  if (term.length < MIN_SEARCH_LEN) {
    return <div className="text-xs text-muted py-3">Digita almeno 2 lettere dello pseudonimo per iniziare la ricerca.</div>;
  }
  if (results.length === 0) {
    return <div className="text-xs text-muted py-3">Nessun profilo trovato.</div>;
  }
  return (
    <>
      {results.map((p) => <ProfileResultRow key={p.identityId} profile={p} onSelect={onSelect} />)}
    </>
  );
}

export function NewChatDialog({ onClose, onOpened, myIdentityId }) {
  const [q, setQ] = useState("");
  const [openError, setOpenError] = useState(null);
  const [openLoading, setOpenLoading] = useState(false);
  const [recoveryProfile, setRecoveryProfile] = useState(null);
  const { results, loading: searchLoading, error: searchError } = useProfileSearch(q, myIdentityId);

  const startWith = async (profile) => {
    if (!profile.x25519PublicKeyHex) {
      setOpenError("Questo utente non ha ancora abilitato i messaggi cifrati (deve accedere di nuovo).");
      return;
    }
    setOpenLoading(true);
    setOpenError(null);
    try {
      const conv = await openConversation(profile.identityId);
      onOpened({ ...conv, peer: { ...conv.peer, ...profile } });
    } catch (e) {
      const message = e?.response?.data?.message || e?.message || "Impossibile aprire la chat";
      if (/impossibile sbloccare la chiave privata/i.test(String(message))) {
        setRecoveryProfile(profile);
      } else {
        setOpenError(message);
      }
    } finally {
      setOpenLoading(false);
    }
  };

  const displayError = openError || searchError;

  const retryAfterRecovery = async () => {
    const profile = recoveryProfile;
    if (!profile) return;
    setRecoveryProfile(null);
    await startWith(profile);
  };

  return (
    <>
    <div
      className="fixed inset-0 z-50 bg-ink/40 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="dm-new-dialog"
    >
      <div className="bg-white rounded-3xl shadow-xl w-full max-w-md p-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <div className="font-display font-semibold text-lg">Nuova conversazione</div>
          <button onClick={onClose} className="text-xs text-muted hover:text-ink" data-testid="dm-new-close">Chiudi</button>
        </div>
        <div className="relative mb-3">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted" />
          <input
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Cerca uno pseudonimo…"
            className="w-full pl-9 pr-3 py-2.5 rounded-full border border-edge bg-cream text-sm outline-none focus:border-ink focus:ring-2 focus:ring-ink/10"
            data-testid="dm-new-search"
          />
        </div>
        {displayError && <div className="text-xs text-primary mb-2" data-testid="dm-new-error">{displayError}</div>}
        <div className="max-h-72 overflow-y-auto -mx-2 px-2">
          <ResultsList
            query={q}
            loading={searchLoading || openLoading}
            results={results}
            onSelect={startWith}
          />
        </div>
      </div>
    </div>
    <IdentityRecoveryDialog
      open={Boolean(recoveryProfile)}
      onClose={() => setRecoveryProfile(null)}
      onRecovered={retryAfterRecovery}
      actionLabel="Ripristina e apri la conversazione"
    />
    </>
  );
}
